import { useEffect, useRef } from 'preact/hooks';
import { route } from 'preact-router';
import { currentVisit, isAdmin, isDesktop, loading, tenant } from '../store/state';
import {
  ensureVisitLoaded,
  tomarVisita,
  checkin,
  guardarTrabajo,
  agregarFoto,
  guardarFirma,
  checkout,
} from '../store/actions/visit.actions';
import { compressImage } from '../features/image-compression/compressImage';
import { SignaturePad, type SignaturePadHandle } from '../features/signature-pad/SignaturePad';
import { generateReport } from '../features/report/generateReport';
import { Shell } from '../layout/Shell';
import { Icon } from '../components/Icon';
import { fmt, eL, dur } from '../utils/format';

interface Props {
  id: string;
}

export function VisitaView({ id }: Props) {
  useEffect(() => {
    ensureVisitLoaded(id);
  }, [id]);

  const v = currentVisit.value;
  const trabajoRef = useRef<HTMLTextAreaElement>(null);
  const ticketRef = useRef<HTMLInputElement>(null);
  const firmaNombreRef = useRef<HTMLInputElement>(null);
  const sigRef = useRef<SignaturePadHandle>(null);
  const fotoAntesRef = useRef<HTMLInputElement>(null);
  const fotoDespuesRef = useRef<HTMLInputElement>(null);

  if (!v || v.id !== id) {
    return <Shell tab="agenda">{!loading.value && <p class="empty">No encontrada.</p>}</Shell>;
  }

  const p = v.estado === 'agendada';
  const ec = v.estado === 'en_curso';
  const co = v.estado === 'completada';
  const fa = v.estado === 'fallida';
  const d = dur(v.fechaCheckin, v.fechaCheckout);
  const sinDueno = !v.tecnicoId && p;

  const onFoto = async (tipo: 'antes' | 'despues', file: File | undefined) => {
    if (!file) return;
    const dataUrl = await compressImage(file);
    await agregarFoto(v.id, dataUrl, tipo);
  };

  const onGuardarFirma = async () => {
    const nombre = firmaNombreRef.current!.value.trim();
    if (!nombre) {
      alert('Ingresá el nombre.');
      return;
    }
    if (sigRef.current!.isEmpty()) {
      alert('Firmá primero.');
      return;
    }
    await guardarFirma(v.id, sigRef.current!.getData(), nombre);
  };

  return (
    <Shell tab="agenda">
      <button
        class="secondary"
        style={{ marginBottom: 12, ...(isDesktop.value ? { width: 'auto', padding: '8px 16px' } : {}) }}
        onClick={() => route('/agenda')}
      >
        <Icon name="arrow-left" size={16} />
        Volver
      </button>

      <div class="card">
        {v.tipo === 'emergencia' && (
          <span class="badge" style={{ background: 'var(--danger-bg)', color: 'var(--danger)', marginBottom: 8 }}>
            <Icon name="zap" size={12} />
            Emergencia
          </span>
        )}
        <div class="row">
          <strong>{v.client?.nombre}</strong>
          <span class={'badge estado-' + v.estado}>{sinDueno ? 'Sin tomar' : eL(v.estado)}</span>
        </div>
        {v.client?.direccion && (
          <p class="muted" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon name="map-pin" size={13} />
            {v.client.direccion}
          </p>
        )}
        <p class="muted" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon name="clock" size={13} />
          Programada: {fmt(v.fechaProgramada)}
        </p>
        <p class="muted" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon name="user-round" size={13} />
          {v.tecnico ? v.tecnico.nombre : <em>Sin asignar</em>}
        </p>
        {v.fechaCheckin && <p class="muted">Check-in: {fmt(v.fechaCheckin)}</p>}
        {v.fechaCheckout && (
          <p class="muted">
            Check-out: {fmt(v.fechaCheckout)}
            {d !== null ? ` (${d} min)` : ''}
          </p>
        )}
        {v.numeroTicket && (
          <p class="muted" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon name="ticket" size={13} />#{v.numeroTicket}
          </p>
        )}
        {fa && (
          <div class="error-box" style={{ marginTop: 8 }}>
            <Icon name="triangle-alert" size={15} />
            Motivo: {v.motivoFallida}
          </div>
        )}
      </div>

      {v.client?.notasAcceso && (p || ec) && (
        <div class="card" style={{ background: 'var(--warning-bg)', borderColor: 'var(--warning)' }}>
          <strong style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon name="triangle-alert" size={14} />
            Notas de acceso
          </strong>
          <p style={{ margin: '4px 0 0', fontSize: 14 }}>{v.client.notasAcceso}</p>
        </div>
      )}

      {sinDueno && (
        <button class="primary" onClick={() => tomarVisita(v.id)}>
          <Icon name="wrench" size={16} />
          Tomar esta visita
        </button>
      )}

      {p && v.tecnicoId && (
        <>
          <button class="primary" onClick={() => checkin(v.id)}>
            <Icon name="badge-check" size={16} />
            Marcar check-in
          </button>
          {isAdmin.value && (
            <button class="secondary" onClick={() => route(`/visitas/${v.id}/reasignar`)}>
              <Icon name="user-round" size={15} />
              Reasignar
            </button>
          )}
          <button class="secondary" onClick={() => route(`/visitas/${v.id}/posponer`)}>
            <Icon name="clock" size={15} />
            Posponer
          </button>
          <button class="danger-outline" onClick={() => route(`/visitas/${v.id}/cancelar`)}>
            <Icon name="x" size={15} />
            Cancelar visita
          </button>
        </>
      )}

      {(ec || co || fa) && (
        <>
          <div class="card">
            <label>Trabajo realizado</label>
            <textarea ref={trabajoRef} disabled={co || fa}>
              {v.descripcionTrabajo || ''}
            </textarea>
            <label>Número de ticket</label>
            <input
              ref={ticketRef}
              defaultValue={v.numeroTicket || ''}
              disabled={co || fa}
              placeholder="Ej: TK-2024-001"
            />
            {!co && !fa && (
              <button
                class="secondary"
                onClick={() =>
                  guardarTrabajo(v.id, trabajoRef.current!.value, ticketRef.current!.value)
                }
              >
                <Icon name="clipboard-check" size={15} />
                Guardar
              </button>
            )}
          </div>

          <div class="card">
            <label>Fotos</label>
            {!(v.fotos || []).length && ec && (
              <p class="muted" style={{ margin: '0 0 4px' }}>
                Sacá una foto de antes y una de después para documentar el trabajo.
              </p>
            )}
            <div class="photo-row">
              {(v.fotos || []).map((f, i) => (
                <div key={i} style={{ position: 'relative', display: 'inline-block' }}>
                  <img class="photo-thumb" src={f.url} />
                  <span
                    style={{
                      position: 'absolute',
                      bottom: 6,
                      left: 6,
                      background: 'rgba(0,0,0,.6)',
                      color: 'white',
                      fontSize: 10,
                      padding: '1px 5px',
                      borderRadius: 4,
                    }}
                  >
                    {f.tipo}
                  </span>
                </div>
              ))}
            </div>
            {!co && !fa && (
              <>
                <input
                  ref={fotoAntesRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  style={{ display: 'none' }}
                  onChange={() => onFoto('antes', fotoAntesRef.current?.files?.[0])}
                />
                <input
                  ref={fotoDespuesRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  style={{ display: 'none' }}
                  onChange={() => onFoto('despues', fotoDespuesRef.current?.files?.[0])}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button class="secondary" style={{ flex: 1 }} onClick={() => fotoAntesRef.current?.click()}>
                    <Icon name="camera" size={15} />
                    Antes
                  </button>
                  <button class="secondary" style={{ flex: 1 }} onClick={() => fotoDespuesRef.current?.click()}>
                    <Icon name="camera" size={15} />
                    Después
                  </button>
                </div>
              </>
            )}
          </div>

          <div class="card">
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name="pen-line" size={14} />
              Firma
            </label>
            {v.firmaUrl ? (
              <>
                <img
                  src={v.firmaUrl}
                  style={{ width: '100%', maxWidth: 400, border: '1px solid var(--border)', borderRadius: 8 }}
                />
                <p class="muted">
                  {v.firmaNombreCliente} · {fmt(v.firmaFecha)}
                </p>
              </>
            ) : (
              ec && (
                <>
                  <input
                    ref={firmaNombreRef}
                    placeholder="Nombre de quien firma"
                    style={{ marginBottom: 8, maxWidth: 400 }}
                  />
                  <SignaturePad ref={sigRef} />
                  <button class="secondary" onClick={() => sigRef.current?.clear()}>
                    <Icon name="undo-2" size={15} />
                    Limpiar
                  </button>
                  <button class="primary" onClick={onGuardarFirma}>
                    <Icon name="pen-line" size={16} />
                    Guardar firma
                  </button>
                </>
              )
            )}
          </div>

          {ec && (
            <>
              <button class="primary" disabled={!v.firmaUrl} onClick={() => checkout(v.id)}>
                <Icon name="circle-check" size={16} />
                Cerrar visita
              </button>
              {!v.firmaUrl && <p class="muted" style={{ textAlign: 'center' }}>Falta la firma.</p>}
              <button class="danger-outline" onClick={() => route(`/visitas/${v.id}/fallida`)}>
                <Icon name="triangle-alert" size={15} />
                No se pudo realizar
              </button>
            </>
          )}
          {co && (
            <button class="primary" onClick={() => generateReport(v, tenant.value)}>
              <Icon name="file-text" size={16} />
              Generar reporte PDF
            </button>
          )}
        </>
      )}
    </Shell>
  );
}
